import {Injectable} from '@angular/core';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {BehaviorSubject, merge, Observable, of, Subject} from 'rxjs';
import {debounceTime, map, scan, shareReplay, switchMap} from 'rxjs/operators';

import {UserPermissionDto} from 'src/app/permission/classes/userPermissionDto.models';
import {UserPermissionDataService} from 'src/app/services/gp/user-permission-data.service';
import {SearchOptionsRequest} from 'src/app/shared/models/SearchOptionsRequest.model';

@Injectable({
  providedIn: 'root'
})
export class UserAndPermissionTableService extends OnDestroyMixin {
  private _refreshData$ = new Subject<void>();
  refreshData$ = this._refreshData$.asObservable();

  private pageSize$ = new BehaviorSubject<number>(0);
  private pageNumber$ = new BehaviorSubject<number>(0);
  private filter$ = new BehaviorSubject<string>('');
  private orderBy$ = new BehaviorSubject<string>('email');
  private sortDirection$ = new BehaviorSubject<string>('asc');

  private pageSizeSource$ = this.pageSize$.pipe(map(pageSize => ({pageSize}) as SearchOptionsRequest));
  private pageNumberSource$ = this.pageNumber$.pipe(map(pageNumber => ({pageNumber}) as SearchOptionsRequest));
  private filterSource$ = this.filter$.pipe(map(filter => ({filter}) as SearchOptionsRequest));
  private orderBySource$ = this.orderBy$.pipe(map(orderBy => ({orderBy}) as SearchOptionsRequest));
  private sortDirectionSource$ = this.sortDirection$.pipe(map(sortOrder => ({sortOrder}) as SearchOptionsRequest));

  private searchOptionsSources$ = merge(
    this.pageSizeSource$,
    this.pageNumberSource$,
    this.filterSource$,
    this.orderBySource$,
    this.sortDirectionSource$
  );

  searchOptions$ = this.searchOptionsSources$.pipe(
    untilComponentDestroyed(this),
    scan((acc, value) => {
      const pageNumber = value.pageNumber === undefined ? 0 : value.pageNumber;
      return {...acc, ...value, pageNumber} as SearchOptionsRequest;
    }, new SearchOptionsRequest()),
    debounceTime(10),
    shareReplay(1)
  );

  constructor(
    private userPermissionDataService: UserPermissionDataService
  ) {
    super();
  }

  refreshData() {
    this._refreshData$.next();
  }

  getPermissionsList(options: SearchOptionsRequest = null): Observable<UserPermissionDto[]> {
    return this.userPermissionDataService.getPermissionsList(options);
  }

  updatePageSize(pageSize: number) {
    this.pageSize$.next(pageSize);
  }

  updatePageNumber(pageNumber: number) {
    this.pageNumber$.next(pageNumber);
  }

  updateFilter(filter: string) {
    this.filter$.next(filter);
  }

  sort(orderBy: string, sortOrder: string) {
    this.orderBy$.next(orderBy);
    this.sortDirection$.next(sortOrder);
  }

  deleteUserPermission(model: UserPermissionDto): Observable<UserPermissionDto> {
    return this.userPermissionDataService.deleteUserPermission(model);
  }
}
